import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CalendarPlus, Plus, Trash2, Send, Users, Pencil, MessageCircle, ExternalLink, Loader2, UserPlus, Trophy, FileText, Wallet } from "lucide-react";
import { FeeChargeWhatsappModal } from "@/components/fees/FeeChargeWhatsappModal";
import { OpponentAthleteModal } from "@/components/events/OpponentAthleteModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { sendWhatsappSequence } from "@/lib/api/whatsapp.functions";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { listRentalModalities } from "@/lib/api/rentals.functions";
import {
  listEvents, listTcs, upsertEvent, deleteEvent,
} from "@/lib/api/events.functions";
import { listFees, upsertFee, deleteFee, linkFeeToEvent } from "@/lib/api/summons.functions";
import { lookupCep, maskCep } from "@/lib/viacep";
import { brToday } from "@/lib/tz";
import { listSummonsByEvent } from "@/lib/api/summons.functions";
import { getMyTrainingCenter } from "@/lib/api/training-center.functions";
import { toMediaUrl } from "@/lib/media-url";
import { openEventPdf } from "@/lib/pdf/event-pdf";
import { EventFeesSettlementDialog } from "@/components/events/EventFeesSettlementDialog";

export const Route = createFileRoute("/_authenticated/m/criar-eventos")({
  component: CriarEventosPage,
});

const EVENT_TYPES = ["Futebol de Campo", "Futebol Society", "Futsal / Salão"];
const FOOTWEAR = [
  "Chuteira trava alta para grama",
  "Chuteira trava baixa de sintético",
  "Chuteira sem trava futebol de salão",
];
const FOOTWEAR_BY_MODALITY: Record<string, string> = {
  futsal: "Chuteira sem trava futebol de salão",
  society: "Chuteira trava baixa de sintético",
  campo: "Chuteira trava alta para grama",
};
const CATEGORIES = [
  "Sub-5","Sub-6","Sub-7","Sub-8","Sub-9","Sub-10","Sub-11","Sub-12",
  "Sub-13","Sub-14","Sub-15","Sub-16","Sub-17","Adulto",
];

type Cat = {
  id?: string | null;
  field_name: string;
  modality_id?: string | null;
  category: string;
  match_date: string;
  start_time: string;
  end_time: string;
  birth_year_start: number | null;
  birth_year_end: number | null;
};

type EventCat = "amistoso" | "campeonato_interno" | "campeonato_externo" | "jogos_internos";
type FormState = {
  id: string | null;
  event_category: EventCat;
  internal_teams: { name: string }[];
  invited_tc_id: string;
  invited_name: string;
  invited_responsible: string;
  invited_whatsapp: string;
  invite_date: string;
  name: string;
  start_date: string;
  end_date: string;
  location_name: string;
  cep: string; street: string; number: string; complement: string;
  neighborhood: string; city: string; state: string;
  event_type: string;
  footwear: string;
  athletes_playing: number;
  max_inscribed: number;
  registration_deadline: string;
  period_minutes: number;
  goalkeeper_above: boolean;
  female_above: boolean;
  blue_card: boolean;
  fields: string[];
  categories: Cat[];
};

const today = () => brToday();

/** "HH:MM" -> minutos. Retorna null quando vazio/inválido. */
const toMin = (t?: string | null): number | null => {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};
/** Soma 1 hora a "HH:MM" (limitado a 23:59). */
const addHour = (t: string): string => {
  const mm = toMin(t);
  if (mm == null) return t;
  const n = Math.min(mm + 60, 23 * 60 + 59);
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
};
/** Sobreposição real: fim == início NÃO é conflito. */
const overlaps = (aStart?: string | null, aEnd?: string | null, bStart?: string | null, bEnd?: string | null) => {
  const as = toMin(aStart), bs = toMin(bStart);
  if (as == null || bs == null) return false;
  const ae = toMin(aEnd) ?? as + 60;
  const be = toMin(bEnd) ?? bs + 60;
  return as < be && bs < ae;
};

// Guess birth-year range from a category label like "Sub-13".
function guessBirthYears(category: string): { start: number | null; end: number | null; label: string } {
  const m = /(\d+)/.exec(category || "");
  if (!m) return { start: null, end: null, label: "" };
  const age = Number(m[1]);
  const y = new Date().getFullYear();
  const year = y - age;
  return { start: year, end: year, label: `${year}` };
}
function birthYearsLabel(c: { birth_year_start: number | null; birth_year_end: number | null; category: string }): string {
  if (c.birth_year_start && c.birth_year_end && c.birth_year_start !== c.birth_year_end) return `${c.birth_year_start} / ${c.birth_year_end}`;
  if (c.birth_year_start) return `${c.birth_year_start}`;
  return guessBirthYears(c.category).label;
}


function buildInviteMessage(form: FormState, hostName: string): string {
  const lines: string[] = [];
  lines.push(`🏆 *CONVITE - ${form.name}*`);
  lines.push(`Organizado por: *${hostName}*`);
  if (form.start_date) lines.push(`📅 Data: ${new Date(form.start_date + "T00:00:00").toLocaleDateString("pt-BR")}${form.end_date && form.end_date !== form.start_date ? ` a ${new Date(form.end_date + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}`);
  if (form.location_name) lines.push(`📍 Local: ${form.location_name}`);
  const addr = [form.street, form.number, form.neighborhood, form.city, form.state].filter(Boolean).join(", ");
  if (addr) lines.push(`   ${addr}`);
  lines.push("");
  lines.push(`⚽ *Modalidade:* ${form.event_type}`);
  lines.push(`👟 *Calçado:* ${form.footwear}`);
  lines.push(`⏱ *Tempo:* 2x ${form.period_minutes} min`);
  lines.push(`👥 *Atletas:* ${form.athletes_playing} em campo (máx. ${form.max_inscribed} inscritos)`);
  if (form.goalkeeper_above) lines.push(`🧤 Goleiro pode 1 categoria acima`);
  if (form.female_above) lines.push(`♀️ Feminino pode 1 categoria acima`);
  if (form.categories.length > 0) {
    lines.push("");
    lines.push(`*Categorias e horários:*`);
    for (const c of form.categories) {
      const by = birthYearsLabel(c);
      lines.push(`• ${c.category}${by ? ` (${by})` : ""} — ${c.field_name} · ${c.match_date ? new Date(c.match_date + "T00:00:00").toLocaleDateString("pt-BR") : ""} ${c.start_time ?? ""}${c.end_time ? `-${c.end_time}` : ""}`);
    }
  }
  lines.push("");
  lines.push(`📋 *Regras de cartões:*`);
  const isFutsal = /futsal|quadra|sal[ãa]o/i.test(form.event_type);
  if (isFutsal) {
    lines.push(`• 🟨 Amarelo = Advertência (2 amarelos = Vermelho)`);
    lines.push(`• 🟥 Vermelho = Expulsão (2 min fora ou até sofrer o gol; entra outro atleta)`);
    lines.push(`• 6ª falta coletiva = Tiro Livre Direto (faltas zeram a cada tempo)`);
    if (form.blue_card) lines.push(`• 🟦 Cartão Azul: jogador substituído *não pode retornar* à partida`);
  } else {
    lines.push(`• 🟨 2 Amarelos = Vermelho`);
    lines.push(`• 🟥 Vermelho = Expulsão`);
    lines.push(`• Sem contagem de faltas coletivas`);
    if (form.blue_card) lines.push(`• 🟦 Cartão Azul: jogador substituído *não pode retornar* à partida`);
  }
  lines.push("");
  lines.push(`📎 Vou enviar a seguir o *link para o cadastro dos atletas*. Você pode preencher pelo próprio CT ou encaminhar o link para os pais/responsáveis.`);
  lines.push("");
  lines.push(`Responda para confirmar a participação. Obrigado!`);
  return lines.join("\n");
}

function buildCtLinkMessage(eventId: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const ctLink = `${origin}/inscrever-adversario/${eventId}`;
  return [
    `🏢 *Link do Centro de Treinamento (uso interno)*`,
    ``,
    `Use este link para cadastrar os atletas e acompanhar a lista completa de inscritos:`,
    ctLink,
  ].join("\n");
}

function buildParentsLinkMessage(eventId: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const parentLink = `${origin}/inscrever-adversario/${eventId}?p=1`;
  return [
    `👨‍👩‍👧 *Link para encaminhar aos pais/responsáveis*`,
    ``,
    `Olá! Segue o link para o cadastro do seu atleta no evento:`,
    parentLink,
    ``,
    `📸 *Dica:* enviando a *foto da carteira de identidade* no cadastro, não será necessário apresentá-la no dia do jogo.`,
  ].join("\n");
}


const emptyForm = (): FormState => ({
  id: null, event_category: "amistoso", internal_teams: [],
  invited_tc_id: "", invited_name: "", invited_responsible: "",
  invited_whatsapp: "", invite_date: today(), name: "",
  start_date: today(), end_date: today(),
  location_name: "", cep: "", street: "", number: "", complement: "",
  neighborhood: "", city: "", state: "",
  event_type: "Futebol de Campo", footwear: "Chuteira trava alta para grama",
  athletes_playing: 11, max_inscribed: 33,
  registration_deadline: today(), period_minutes: 20,
  goalkeeper_above: true, female_above: true, blue_card: false,
  fields: ["Campo 1"],
  categories: [(() => { const g = guessBirthYears("Sub-13"); return {
    field_name: "Campo 1", category: "Sub-13",
    match_date: today(), start_time: "08:00", end_time: "09:00",
    birth_year_start: g.start, birth_year_end: g.end,
  }; })()],
});

// Custom options (event types / footwear) persisted per-CT via localStorage.
const LS_KEY = "eventos:customOptions";
type CustomOpts = { event_types: string[]; footwear: string[] };
const loadCustomOpts = (): CustomOpts => {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    const p = raw ? JSON.parse(raw) : {};
    return { event_types: p.event_types ?? [], footwear: p.footwear ?? [] };
  } catch { return { event_types: [], footwear: [] }; }
};
const saveCustomOpts = (o: CustomOpts) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch {}
};

function CriarEventosPage() {
  const listEv = useServerFn(listEvents);
  const listTc = useServerFn(listTcs);
  const save = useServerFn(upsertEvent);
  const del = useServerFn(deleteEvent);
  const listQuadras = useServerFn(listRentalModalities);

  const [tab, setTab] = useState("criar");
  const [tc, setTc] = useState<any>(null);
  const [tcs, setTcs] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [newField, setNewField] = useState("");
  const [quadras, setQuadras] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastSavedEvent, setLastSavedEvent] = useState<any>(null);
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteMsg2, setInviteMsg2] = useState("");
  const [inviteMsg3, setInviteMsg3] = useState("");
  const [invitePhone, setInvitePhone] = useState("");

  const [opponentEvent, setOpponentEvent] = useState<{ id: string; name: string; categories?: any[]; internal?: boolean } | null>(null);
  const navigate = useNavigate();
  const [customOpts, setCustomOpts] = useState<CustomOpts>({ event_types: [], footwear: [] });
  const [newEventType, setNewEventType] = useState("");
  const [newFootwear, setNewFootwear] = useState("");

  const reload = async () => {
    const r = await listEv();
    setEvents(r.events);
    setTc(r.tc);
  };
  useEffect(() => { reload().catch(() => {}); listTc().then((r) => setTcs(r.tcs)); listQuadras().then((r: any) => setQuadras((r.modalities ?? []).filter((m: any) => m.active !== false).map((m: any) => ({ id: m.id, name: m.name })))).catch(() => {}); setCustomOpts(loadCustomOpts()); }, []);

  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictMsg, setConflictMsg] = useState("");

  // Auto-preencher "Usar endereço do CT" quando for campeonato interno
  useEffect(() => {
    if (form.event_category === "campeonato_interno" && tc && !form.cep && !form.street) {
      setForm((p) => ({
        ...p,
        location_name: p.location_name || tc.name || "",
        cep: tc.cep ?? "", street: tc.street ?? "", number: tc.number ?? "",
        neighborhood: tc.neighborhood ?? "", city: tc.city ?? "", state: tc.state ?? "",
      }));
    }
  }, [form.event_category, tc]);

  // Sincroniza data dos jogos com data inicial do evento (quando vazio ou anterior)
  useEffect(() => {
    if (!form.start_date) return;
    setForm((p) => ({
      ...p,
      categories: p.categories.map((c) =>
        !c.match_date || c.match_date < p.start_date ? { ...c, match_date: p.start_date } : c
      ),
    }));
  }, [form.start_date]);

  const allEventTypes = useMemo(() => [...EVENT_TYPES, ...customOpts.event_types], [customOpts]);
  const allFootwear = useMemo(() => [...FOOTWEAR, ...customOpts.footwear], [customOpts]);
  const addEventType = () => {
    const v = newEventType.trim();
    if (!v || allEventTypes.includes(v)) return;
    const next = { ...customOpts, event_types: [...customOpts.event_types, v] };
    setCustomOpts(next); saveCustomOpts(next); setNewEventType(""); upd("event_type", v);
  };
  const addFootwearOpt = () => {
    const v = newFootwear.trim();
    if (!v || allFootwear.includes(v)) return;
    const next = { ...customOpts, footwear: [...customOpts.footwear, v] };
    setCustomOpts(next); saveCustomOpts(next); setNewFootwear(""); upd("footwear", v);
  };

  const useTcAddress = () => {
    if (!tc) return toast.error("Cadastre o CT primeiro");
    setForm((p) => ({
      ...p,
      location_name: p.location_name || tc.name || "",
      cep: tc.cep ?? "", street: tc.street ?? "", number: tc.number ?? "",
      neighborhood: tc.neighborhood ?? "", city: tc.city ?? "", state: tc.state ?? "",
    }));
    toast.success("Endereço do CT preenchido");
  };

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const detectModality = (t: string): "futsal" | "society" | "campo" | "other" => {
    if (/futsal|quadra|sal[ãa]o/i.test(t)) return "futsal";
    if (/society/i.test(t)) return "society";
    if (/campo/i.test(t)) return "campo";
    return "other";
  };

  const onEventTypeChange = (v: string) => {
    const m = detectModality(v);
    setForm((p) => {
      let athletes = p.athletes_playing;
      let max = p.max_inscribed;
      let blue = p.blue_card;
      if (m === "futsal") { athletes = 5; max = 15; blue = false; }
      else if (m === "campo") { athletes = 11; max = 33; }
      else if (m === "society") { athletes = 7; max = 21; }
      const footwear = FOOTWEAR_BY_MODALITY[m] ?? p.footwear;
      return { ...p, event_type: v, athletes_playing: athletes, max_inscribed: max, blue_card: blue, footwear };
    });
  };

  const setModalitySize = (size: number) => {
    setForm((p) => ({ ...p, athletes_playing: size, max_inscribed: size * 3 }));
  };

  const addField = () => {
    const typed = newField.trim();
    let v = typed;
    if (!v) {
      // Auto-name next field if input is empty
      let i = form.fields.length + 1;
      while (form.fields.includes(`Campo ${i}`)) i++;
      v = `Campo ${i}`;
    }
    if (form.fields.includes(v)) return toast.error("Campo já existe");
    upd("fields", [...form.fields, v]);
    setNewField("");
  };
  const removeField = (n: string) =>
    upd("fields", form.fields.filter((f) => f !== n));
  const renameField = (oldName: string) => {
    const next = prompt("Novo nome do campo:", oldName)?.trim();
    if (!next || next === oldName) return;
    if (form.fields.includes(next)) return toast.error("Já existe um campo com esse nome");
    setForm((p) => ({
      ...p,
      fields: p.fields.map((f) => f === oldName ? next : f),
      categories: p.categories.map((c) => c.field_name === oldName ? { ...c, field_name: next } : c),
    }));
  };

  const addCategory = () => {
    const g = guessBirthYears("Adulto");
    upd("categories", [
      ...form.categories,
      {
        field_name: quadras[0]?.name ?? form.fields[0] ?? "",
        modality_id: quadras[0]?.id ?? null,
        category: "Adulto",
        match_date: form.start_date || today(),
        start_time: "08:00", end_time: "09:00",
        birth_year_start: g.start, birth_year_end: g.end,
      },
    ]);
  };
  const updCat = (i: number, k: keyof Cat, v: any) =>
    upd("categories", form.categories.map((c, idx) => {
      if (idx !== i) return c;
      const next = { ...c, [k]: v };
      // Sempre recalcular anos ao mudar a categoria (ano fixo por faixa etária).
      if (k === "category") {
        const g = guessBirthYears(v);
        next.birth_year_start = g.start; next.birth_year_end = g.end;
      }
      // Ao definir o horário inicial, sugerir 1h de duração (campo editável).
      if (k === "start_time" && v) {
        if (!next.end_time || (toMin(next.end_time) ?? 0) <= (toMin(v) ?? 0)) {
          next.end_time = addHour(v);
        }
      }
      return next;

    }));
  const removeCat = (i: number) =>
    upd("categories", form.categories.filter((_, idx) => idx !== i));

  const editEvent = (e: any) => {
    setForm({
      id: e.id,
      event_category: (e.event_category as EventCat) ?? "amistoso",
      internal_teams: Array.isArray(e.internal_teams) ? e.internal_teams : [],
      invited_tc_id: e.invited_tc_id ?? "",
      invited_name: e.invited_name ?? "",
      invited_responsible: e.invited_responsible ?? "",
      invited_whatsapp: e.invited_whatsapp ?? "",
      invite_date: e.invite_date ?? today(),
      name: e.name ?? "",
      start_date: e.start_date ?? today(),
      end_date: e.end_date ?? today(),
      location_name: e.location_name ?? "",
      cep: e.cep ?? "", street: e.street ?? "", number: e.number ?? "",
      complement: e.complement ?? "", neighborhood: e.neighborhood ?? "",
      city: e.city ?? "", state: e.state ?? "",
      event_type: e.event_type ?? "Futebol de Campo",
      footwear: e.footwear ?? "Chuteira trava alta para grama",
      athletes_playing: e.athletes_playing ?? 11,
      max_inscribed: e.max_inscribed ?? 33,
      registration_deadline: e.registration_deadline ?? today(),
      period_minutes: e.period_minutes ?? 20,
      goalkeeper_above: e.goalkeeper_above ?? true,
      female_above: e.female_above ?? true,
      blue_card: e.blue_card ?? false,
      fields: e.fields?.length ? e.fields : ["Campo 1"],
      categories: (e.event_categories ?? []).map((c: any) => ({
        id: c.id,
        field_name: c.field_name ?? "",
        modality_id: c.modality_id ?? null,
        category: c.category ?? "",
        match_date: c.match_date ?? "",
        start_time: c.start_time?.slice(0, 5) ?? "",
        end_time: c.end_time?.slice(0, 5) ?? "",
        birth_year_start: c.birth_year_start ?? null,
        birth_year_end: c.birth_year_end ?? null,
      })),
    });
    setTab("criar");
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Excluir este evento?")) return;
    await del({ data: { id } });
    toast.success("Evento excluído");
    reload();
  };

  /**
   * Conflito de horário = sobreposição real no mesmo campo/data.
   * Fim de um jogo igual ao início do outro NÃO é conflito.
   */
  const findConflict = (): string | null => {
    const cats = form.categories;
    for (let i = 0; i < cats.length; i++) {
      const c = cats[i];
      if (!c.field_name || !c.match_date || !c.start_time) continue;
      const dBr = new Date(c.match_date + "T00:00:00").toLocaleDateString("pt-BR");
      for (let j = 0; j < i; j++) {
        const p = cats[j];
        if (p.field_name !== c.field_name || p.match_date !== c.match_date) continue;
        if (overlaps(p.start_time, p.end_time, c.start_time, c.end_time)) {
          return `NÃO É POSSÍVEL GRAVAR PARA ESSE HORÁRIO PORQUE JÁ EXISTE UM JOGO MARCADO NO CAMPO "${c.field_name.toUpperCase()}" EM ${dBr} DAS ${p.start_time} ÀS ${p.end_time || "?"} (${p.category.toUpperCase()} x ${c.category.toUpperCase()}).`;
        }
      }
      for (const ev of events) {
        if (ev.id === form.id) continue;
        const other = (ev.event_categories ?? []).find(
          (oc: any) =>
            oc.field_name === c.field_name &&
            oc.match_date === c.match_date &&
            overlaps(oc.start_time?.slice(0, 5), oc.end_time?.slice(0, 5), c.start_time, c.end_time)
        );
        if (other) {
          return `NÃO É POSSÍVEL GRAVAR PARA ESSE HORÁRIO PORQUE JÁ EXISTE UM EVENTO MARCADO NO CAMPO "${c.field_name.toUpperCase()}" EM ${dBr} DAS ${other.start_time?.slice(0, 5)} ÀS ${other.end_time?.slice(0, 5) || "?"} (${String(ev.name).toUpperCase()}).`;
        }
      }
    }
    return null;
  };

  /** Verificação imediata ao sair do campo de horário. */
  const checkConflictNow = () => {
    const msg = findConflict();
    if (msg) { setConflictMsg(msg); setConflictOpen(true); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do evento");
    // Data do jogo não pode ser inferior à data atual
    const tdy = today();
    const past = form.categories.find((c) => c.match_date && c.match_date < tdy);
    if (past) {
      return toast.error(
        `A data do jogo "${past.category}" (${past.field_name}) não pode ser anterior a hoje.`
      );
    }
    const conflict = findConflict();
    if (conflict) {
      setConflictMsg(conflict);
      setConflictOpen(true);
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      return toast.error("A data final do evento não pode ser anterior à data inicial.");
    }
    if (form.start_date) {
      const bad = form.categories.find(
        (c) => c.match_date && c.match_date < form.start_date
      );
      if (bad) {
        return toast.error(
          `A categoria "${bad.category}" (${bad.field_name}) está agendada para ${new Date(bad.match_date + "T00:00:00").toLocaleDateString("pt-BR")}, antes da data inicial do evento (${new Date(form.start_date + "T00:00:00").toLocaleDateString("pt-BR")}).`
        );
      }
    }
    if (form.end_date) {
      const bad = form.categories.find(
        (c) => c.match_date && c.match_date > form.end_date
      );
      if (bad) {
        return toast.error(
          `A categoria "${bad.category}" (${bad.field_name}) está agendada para ${new Date(bad.match_date + "T00:00:00").toLocaleDateString("pt-BR")}, após a data final do evento (${new Date(form.end_date + "T00:00:00").toLocaleDateString("pt-BR")}).`
        );
      }
    }
    setLoading(true);
    try {
      const res = await save({ data: form });
      toast.success("Evento salvo com sucesso!");
      const saved = { ...form, id: res.id };
      setLastSavedEvent(saved);
      setInvitePhone(form.invited_whatsapp);
      setInviteMsg(buildInviteMessage(form, tc?.name ?? ""));
      setInviteMsg2(buildCtLinkMessage(res.id));
      setInviteMsg3(buildParentsLinkMessage(res.id));

      setForm(emptyForm());
      reload();
      setTab("convidar");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar");
    } finally { setLoading(false); }
  };

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const sendSeqFn = useServerFn(sendWhatsappSequence);

  const openInvite = () => {
    const digits = invitePhone.replace(/\D/g, "");
    if (!digits) return toast.error("Informe o WhatsApp");
    if (!inviteMsg.trim()) return toast.error("Selecione um evento para gerar a mensagem");
    setInviteOpen(true);
  };

  const openInviteWeb = () => {
    const digits = invitePhone.replace(/\D/g, "");
    const number = digits.length <= 11 ? `55${digits}` : digits;
    const combined = [inviteMsg, inviteMsg2, inviteMsg3].filter((m) => m.trim()).join("\n\n———\n\n");
    const url = `https://web.whatsapp.com/send?phone=${number}&text=${encodeURIComponent(combined)}`;
    window.open(url, "_blank");
  };

  const sendInviteApi = async () => {
    setInviteSending(true);
    try {
      const messages = [inviteMsg, inviteMsg2, inviteMsg3].filter((m) => m.trim());
      const r: any = await sendSeqFn({ data: {
        recipient: { name: lastSavedEvent?.invited_responsible || lastSavedEvent?.invited_name || "convidado", phone: invitePhone },
        messages,
      } });

      if (r.sent > 0) {
        toast.success(`Convite enviado (${r.sent} mensagens)`);
        setInviteOpen(false);
      } else {
        toast.error(r.errors?.[0] || "Falha ao enviar");
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar convite");
    } finally {
      setInviteSending(false);
    }
  };



  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <CalendarPlus className="h-7 w-7 text-primary" />
        <h1 className="text-3xl font-bold tracking-tight">Gerenciar Eventos</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="criar">Criar Evento</TabsTrigger>
          <TabsTrigger value="lista">Eventos Criados</TabsTrigger>
          <TabsTrigger value="taxas">Gerenciar Taxas</TabsTrigger>
          <TabsTrigger value="convidar">Convidar CT via Link</TabsTrigger>
        </TabsList>

        <TabsContent value="taxas" className="mt-4">
          <EventFeesPanel events={events} />
        </TabsContent>

        <TabsContent value="criar" className="mt-4">
          <Card>
            <CardHeader><CardTitle>{form.id ? "Editar Evento" : "Criar Evento"}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <Field label="Tipo de Evento *">
                <Select
                  value={form.event_category}
                  onValueChange={(v) => {
                    const cat = v as EventCat;
                    setForm((p) => cat === "jogos_internos"
                      ? {
                          ...p,
                          event_category: cat,
                          invited_tc_id: tc?.id ?? "",
                          invited_name: tc?.name ?? "",
                          invited_responsible: tc?.manager_name ?? p.invited_responsible,
                          invited_whatsapp: tc?.whatsapp ?? p.invited_whatsapp,
                        }
                      : { ...p, event_category: cat });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amistoso">Amistoso</SelectItem>
                    <SelectItem value="jogos_internos">Jogos internos (atletas do centro de treinamento)</SelectItem>
                  </SelectContent>
                </Select>
                {form.event_category === "jogos_internos" && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    O próprio centro de treinamento é o adversário: você convoca atletas para os dois
                    times (mandante e visitante) e preenche a súmula com as duas escalações.
                  </p>
                )}
              </Field>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="CT Organizando">
                  <Input value={tc?.name ?? ""} disabled />
                </Field>
                <Field label="Data do Convite">
                  <Input type="date" value={form.invite_date} onChange={(e) => upd("invite_date", e.target.value)} />
                </Field>
                {form.event_category === "amistoso" && (
                  <>
                    <Field label="CT Convidado">
                      <Select value={form.invited_tc_id || "none"} onValueChange={(v) => upd("invited_tc_id", v === "none" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="-- Selecione ou digite abaixo --" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Selecione ou digite abaixo --</SelectItem>
                          {tc?.id && (
                            <SelectItem value={tc.id}>{tc.name} (evento interno — dois times)</SelectItem>
                          )}
                          {tcs.filter((t) => t.id !== tc?.id).map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.invited_tc_id && form.invited_tc_id === tc?.id && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Evento interno: você poderá convocar atletas para os dois times (mandante e visitante)
                          e preencher a súmula com as duas escalações.
                        </p>
                      )}
                    </Field>

                    <Field label="Nome CT Convidado (Se não listado)">
                      <Input value={form.invited_name} onChange={(e) => upd("invited_name", e.target.value)} />
                    </Field>
                    <Field label="Responsável Convidado">
                      <Input value={form.invited_responsible} onChange={(e) => upd("invited_responsible", e.target.value)} />
                    </Field>
                    <Field label="WhatsApp Convidado">
                      <Input value={form.invited_whatsapp} onChange={(e) => upd("invited_whatsapp", e.target.value)} placeholder="(00) 00000-0000" />
                    </Field>
                  </>
                )}
                <Field label="Nome do Evento">
                  <Input value={form.name} onChange={(e) => upd("name", e.target.value)} />
                </Field>
                <Field label="Data Inicial do Evento">
                  <Input
                    type="date"
                    value={form.start_date}
                    min={today()}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((p) => ({
                        ...p,
                        start_date: v,
                        end_date: p.end_date && p.end_date >= v ? p.end_date : v,
                      }));
                    }}
                  />
                </Field>
                <Field label="Data Final do Evento">
                  <Input
                    type="date"
                    value={form.end_date}
                    min={form.start_date || undefined}
                    onChange={(e) => upd("end_date", e.target.value)}
                  />
                </Field>
              </div>

              {form.event_category === "campeonato_interno" && (
                <fieldset className="border rounded-md p-4 space-y-3">
                  <legend className="px-2 text-sm font-semibold">Times do Campeonato Interno</legend>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Quantidade de Times</Label>
                      <Input
                        type="number" min={0}
                        value={form.internal_teams.length}
                        onChange={(e) => {
                          const n = Math.max(0, Number(e.target.value) || 0);
                          const cur = form.internal_teams;
                          const next = n > cur.length
                            ? [...cur, ...Array.from({ length: n - cur.length }, (_, i) => ({ name: `Time ${cur.length + i + 1}` }))]
                            : cur.slice(0, n);
                          upd("internal_teams", next);
                        }}
                      />
                    </div>
                    <Button type="button" variant="outline"
                      onClick={() => upd("internal_teams", [...form.internal_teams, { name: `Time ${form.internal_teams.length + 1}` }])}>
                      <Plus className="h-4 w-4 mr-1" />Adicionar Time
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.internal_teams.length === 0 && (
                      <p className="text-xs text-muted-foreground">Nenhum time. Defina a quantidade ou clique em "Adicionar Time".</p>
                    )}
                    {form.internal_teams.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={t.name}
                          placeholder={`Nome do Time ${i + 1}`}
                          onChange={(e) =>
                            upd("internal_teams", form.internal_teams.map((tt, idx) => idx === i ? { ...tt, name: e.target.value } : tt))
                          }
                        />
                        <Button type="button" size="icon" variant="ghost"
                          onClick={() => upd("internal_teams", form.internal_teams.filter((_, idx) => idx !== i))}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </fieldset>
              )}

              <fieldset className="border rounded-md p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <legend className="px-2 text-sm font-semibold">Endereço do Local</legend>
                  <Button type="button" size="sm" onClick={useTcAddress} className="bg-green-600 hover:bg-green-700 text-white">
                    Usar endereço do CT
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Local (Nome do Espaço)">
                    <Input value={form.location_name} onChange={(e) => upd("location_name", e.target.value)} />
                  </Field>
                  <Field label="CEP">
                    <Input
                      value={form.cep}
                      onChange={(e) => upd("cep", maskCep(e.target.value))}
                      onBlur={async (e) => {
                        const r = await lookupCep(e.target.value);
                        if (!r) return;
                        if (r.logradouro) upd("street", r.logradouro);
                        if (r.bairro) upd("neighborhood", r.bairro);
                        if (r.localidade) upd("city", r.localidade);
                        if (r.uf) upd("state", r.uf);
                      }}
                      placeholder="00000-000"
                    />
                  </Field>
                  <Field label="Rua" className="md:col-span-2">
                    <Input value={form.street} onChange={(e) => upd("street", e.target.value)} />
                  </Field>
                  <Field label="Número">
                    <Input value={form.number} onChange={(e) => upd("number", e.target.value)} />
                  </Field>
                  <Field label="Bairro">
                    <Input value={form.neighborhood} onChange={(e) => upd("neighborhood", e.target.value)} />
                  </Field>
                  <Field label="Cidade">
                    <Input value={form.city} onChange={(e) => upd("city", e.target.value)} />
                  </Field>
                  <Field label="Estado (UF)">
                    <Input value={form.state} maxLength={2} onChange={(e) => upd("state", e.target.value.toUpperCase())} />
                  </Field>
                </div>
              </fieldset>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Tipo de Evento">
                  <div className="flex gap-2">
                    <Select value={form.event_type} onValueChange={onEventTypeChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allEventTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Novo tipo" value={newEventType}
                      onChange={(e) => setNewEventType(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEventType())}
                      className="w-40" />
                    <Button type="button" variant="outline" size="icon" onClick={addEventType}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </Field>
                <Field label="Tipo de Calçado">
                  <div className="flex gap-2">
                    <Select value={form.footwear} onValueChange={(v) => upd("footwear", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allFootwear.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Novo calçado" value={newFootwear}
                      onChange={(e) => setNewFootwear(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFootwearOpt())}
                      className="w-40" />
                    <Button type="button" variant="outline" size="icon" onClick={addFootwearOpt}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </Field>
                <Field label="Atletas Jogando">
                  {detectModality(form.event_type) === "society" || detectModality(form.event_type) === "campo" ? (
                    <Select value={String(form.athletes_playing)} onValueChange={(v) => setModalitySize(Number(v))}>
                      <SelectTrigger><SelectValue placeholder="Fut 7 / 8 / 9 / 10 / 11" /></SelectTrigger>
                      <SelectContent>
                        {[7, 8, 9, 10, 11].map((n) => (
                          <SelectItem key={n} value={String(n)}>Fut {n} (com goleiro) — {n * 3} inscritos</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type="number" min={0} value={form.athletes_playing}
                      onChange={(e) => upd("athletes_playing", Number(e.target.value))} />
                  )}
                </Field>
                <Field label="Atletas Inscritos (Máx)">
                  <Input type="number" min={0} value={form.max_inscribed}
                    onChange={(e) => upd("max_inscribed", Number(e.target.value))} />
                </Field>
                <Field label="Data Limite Inscrição">
                  <Input type="date" value={form.registration_deadline}
                    onChange={(e) => upd("registration_deadline", e.target.value)} />
                </Field>
                <Field label="Tempo de cada etapa (min)">
                  <Input type="number" min={0} value={form.period_minutes}
                    onChange={(e) => upd("period_minutes", Number(e.target.value))} />
                </Field>
              </div>

              <div className="flex gap-6 bg-muted/30 p-3 rounded-md flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.goalkeeper_above}
                    onCheckedChange={(v) => upd("goalkeeper_above", !!v)} />
                  Goleiro pode 1 cat. acima
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.female_above}
                    onCheckedChange={(v) => upd("female_above", !!v)} />
                  Feminino pode 1 cat. acima
                </label>
                {detectModality(form.event_type) !== "futsal" && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.blue_card}
                      onCheckedChange={(v) => upd("blue_card", !!v)} />
                    🟦 Usar Cartão Azul (jogador substituído não retorna)
                  </label>
                )}
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between items-end gap-3">
                  <h3 className="text-lg font-semibold">Categorias e Horários</h3>
                  <div className="flex gap-2">
                    <Input placeholder="Nome do novo campo" value={newField}
                      onChange={(e) => setNewField(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addField())} />
                    <Button type="button" onClick={addField}><Plus className="h-4 w-4 mr-1" />Campo</Button>
                  </div>
                </div>

                {form.fields.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.fields.map((f) => (
                      <Badge key={f} variant="secondary" className="gap-1">
                        {f}
                        <button type="button" onClick={() => renameField(f)} className="ml-1 hover:text-primary" title="Renomear">
                          ✎
                        </button>
                        <button type="button" onClick={() => removeField(f)} className="ml-1 hover:text-destructive">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  {form.categories.map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end bg-muted/30 p-2 rounded">
                      <div className="col-span-2">
                        <Label className="text-xs">Quadra / Campo</Label>
                        <Select
                          value={c.modality_id ? `m:${c.modality_id}` : `f:${c.field_name}`}
                          onValueChange={(v) => {
                            if (v.startsWith("m:")) {
                              const id = v.slice(2);
                              const q = quadras.find((x) => x.id === id);
                              upd("categories", form.categories.map((cc, idx) => idx === i ? { ...cc, modality_id: id, field_name: q?.name ?? cc.field_name } : cc));
                            } else {
                              const name = v.slice(2);
                              upd("categories", form.categories.map((cc, idx) => idx === i ? { ...cc, modality_id: null, field_name: name } : cc));
                            }
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {quadras.map((q) => <SelectItem key={q.id} value={`m:${q.id}`}>{q.name}</SelectItem>)}
                            {form.fields.map((f) => <SelectItem key={f} value={`f:${f}`}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Categoria</Label>
                        <Select value={c.category} onValueChange={(v) => updCat(i, "category", v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Data</Label>
                        <Input type="date" value={c.match_date}
                          min={form.start_date && form.start_date > today() ? form.start_date : today()}
                          max={form.end_date || undefined}
                          onBlur={checkConflictNow}
                          onChange={(e) => updCat(i, "match_date", e.target.value)} />

                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Início</Label>
                        <Input type="time" value={c.start_time}
                          onBlur={checkConflictNow}
                          onChange={(e) => updCat(i, "start_time", e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Fim</Label>
                        <Input type="time" value={c.end_time}
                          onBlur={checkConflictNow}
                          onChange={(e) => updCat(i, "end_time", e.target.value)} />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button size="icon" variant="ghost" onClick={() => removeCat(i)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="col-span-12">
                        <Label className="text-xs">Ano de nascimento</Label>
                        <Input
                          readOnly
                          disabled
                          value={birthYearsLabel(c) || "—"}
                          className="bg-muted cursor-not-allowed"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">Definido automaticamente pela categoria.</p>
                      </div>

                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addCategory}>
                    <Plus className="h-4 w-4 mr-1" />Adicionar Jogo
                  </Button>
                </div>
              </div>

              <Button className="w-full h-12 text-base" disabled={loading} onClick={handleSave}>
                <Send className="h-4 w-4 mr-2" />
                {form.id ? "Salvar Alterações" : "Criar Evento e Enviar Convite"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lista" className="mt-4 space-y-4">
          <EventsList
            events={events} editEvent={editEvent} handleDelete={handleDelete}
            onOpponent={(ev) => setOpponentEvent({
              id: ev.id,
              name: ev.name,
              internal: ev.event_category === "jogos_internos" || (!!ev.invited_tc_id && ev.invited_tc_id === tc?.id),
              categories: (ev.event_categories ?? []).map((c: any) => ({
                id: c.id, category: c.category, field_name: c.field_name,
              })),
            })}

            onConvocation={(ev) => navigate({ to: "/m/convocacao-jogos", search: { event: ev.id } as any })}
          />
        </TabsContent>


        <TabsContent value="convidar" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Enviar Convite via WhatsApp</CardTitle>
              <p className="text-sm text-muted-foreground">
                Selecione um evento cadastrado para gerar a mensagem de convite.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Evento">
                <Select
                  value={lastSavedEvent?.id ?? ""}
                  onValueChange={(id) => {
                    const ev = events.find((e: any) => e.id === id);
                    if (!ev) return;
                    const mapped: FormState = {
                      ...emptyForm(),
                      id: ev.id,
                      event_category: ev.event_category ?? "amistoso",
                      internal_teams: Array.isArray(ev.internal_teams) ? ev.internal_teams : [],
                      invited_tc_id: ev.invited_tc_id ?? "",
                      invited_name: ev.invited_name ?? "",
                      invited_responsible: ev.invited_responsible ?? "",
                      invited_whatsapp: ev.invited_whatsapp ?? "",
                      invite_date: ev.invite_date ?? today(),
                      name: ev.name ?? "",
                      start_date: ev.start_date ?? "",
                      end_date: ev.end_date ?? "",
                      location_name: ev.location_name ?? "",
                      cep: ev.cep ?? "", street: ev.street ?? "", number: ev.number ?? "",
                      complement: ev.complement ?? "", neighborhood: ev.neighborhood ?? "",
                      city: ev.city ?? "", state: ev.state ?? "",
                      event_type: ev.event_type ?? "Futebol de Campo",
                      footwear: ev.footwear ?? "",
                      athletes_playing: ev.athletes_playing ?? 11,
                      max_inscribed: ev.max_inscribed ?? 33,
                      registration_deadline: ev.registration_deadline ?? "",
                      period_minutes: ev.period_minutes ?? 20,
                      goalkeeper_above: ev.goalkeeper_above ?? true,
                      female_above: ev.female_above ?? true,
                      blue_card: ev.blue_card ?? false,
                      fields: [],
                      categories: (ev.event_categories ?? []).map((c: any) => ({
                        id: c.id, field_name: c.field_name ?? "", category: c.category ?? "",
                        match_date: c.match_date ?? "", start_time: c.start_time ?? "", end_time: c.end_time ?? "",
                        birth_year_start: c.birth_year_start ?? null, birth_year_end: c.birth_year_end ?? null,
                      })),
                    };
                    setLastSavedEvent(mapped);
                    setInvitePhone(ev.invited_whatsapp ?? "");
                    setInviteMsg(buildInviteMessage(mapped, tc?.name ?? ""));
                    setInviteMsg2(buildCtLinkMessage(ev.id));
                    setInviteMsg3(buildParentsLinkMessage(ev.id));

                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione um evento" /></SelectTrigger>
                  <SelectContent>
                    {events.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">Nenhum evento cadastrado.</div>
                    )}
                    {events.map((e: any) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}{e.start_date ? ` — ${new Date(e.start_date + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Número do WhatsApp do Convidado">
                <Input value={invitePhone} onChange={(e) => setInvitePhone(e.target.value)} placeholder="(00) 00000-0000" />
              </Field>
              <Field label="Mensagem 1 — Convite">
                <Textarea rows={8} value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)} placeholder="Selecione um evento acima para gerar a mensagem." />
              </Field>
              <Field label="Mensagem 2 — Link do Centro de Treinamento (uso interno)">
                <Textarea rows={5} value={inviteMsg2} onChange={(e) => setInviteMsg2(e.target.value)} placeholder="Selecione um evento para gerar o link do CT." />
              </Field>
              <Field label="Mensagem 3 — Link para encaminhar aos pais/responsáveis">
                <Textarea rows={6} value={inviteMsg3} onChange={(e) => setInviteMsg3(e.target.value)} placeholder="Selecione um evento para gerar o link dos pais." />
              </Field>

              <div className="flex gap-2">
                <Button className="flex-1" disabled={!inviteMsg.trim() || !invitePhone.trim()} onClick={openInvite}>
                  <Send className="h-4 w-4 mr-2" />Enviar Convite
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!lastSavedEvent?.id}
                  onClick={() => {
                    if (!lastSavedEvent?.id) return;
                    window.open(`${window.location.origin}/inscrever-adversario/${lastSavedEvent.id}`, "_blank", "noopener,noreferrer");
                  }}
                >
                  Ver atletas inscritos
                </Button>
              </div>
            </CardContent>
          </Card>

          <Dialog open={conflictOpen} onOpenChange={setConflictOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-destructive">Conflito de horário</DialogTitle>
              </DialogHeader>
              <p className="text-sm font-semibold uppercase leading-relaxed">{conflictMsg}</p>
              <DialogFooter>
                <Button onClick={() => setConflictOpen(false)}>OK</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" /> Enviar convite via WhatsApp
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Envie pela API integrada ou abra no WhatsApp Web.
                </p>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-sm bg-muted/40 p-3 rounded-lg">
                  <div><strong>{lastSavedEvent?.name}</strong></div>
                  <div className="text-xs text-muted-foreground">
                    Para: {lastSavedEvent?.invited_responsible || lastSavedEvent?.invited_name || "—"} ({invitePhone})
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="rounded-md border bg-background">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">Mensagem 1 — Convite</div>
                    <Textarea
                      value={inviteMsg}
                      onChange={(e) => setInviteMsg(e.target.value)}
                      className="min-h-[160px] border-0 focus-visible:ring-0"
                    />
                  </div>
                  <div className="rounded-md border bg-background">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">Mensagem 2 — Link do CT (uso interno)</div>
                    <Textarea
                      value={inviteMsg2}
                      onChange={(e) => setInviteMsg2(e.target.value)}
                      className="min-h-[100px] border-0 focus-visible:ring-0"
                    />
                  </div>
                  <div className="rounded-md border bg-background">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">Mensagem 3 — Para encaminhar aos pais</div>
                    <Textarea
                      value={inviteMsg3}
                      onChange={(e) => setInviteMsg3(e.target.value)}
                      className="min-h-[120px] border-0 focus-visible:ring-0"
                    />
                  </div>

                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteSending}>Fechar</Button>
                <Button variant="outline" onClick={openInviteWeb} disabled={inviteSending}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Abrir WhatsApp Web
                </Button>
                <Button onClick={sendInviteApi} disabled={inviteSending}>
                  {inviteSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
                  Enviar pela API
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </TabsContent>

      </Tabs>

      <OpponentAthleteModal
        open={!!opponentEvent}
        onOpenChange={(v) => { if (!v) setOpponentEvent(null); }}
        event={opponentEvent}
        tcId={tc?.id ?? null}
        internal={!!opponentEvent?.internal}
      />
    </div>
  );
}

function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

type EventStatus = "encerrado" | "em_andamento" | "futuro";
function computeEventStatus(e: any): EventStatus {
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const start = e.start_date ? new Date(e.start_date + "T00:00:00") : null;
  const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
  if (end && end < today0) return "encerrado";
  if (start && start > today0) return "futuro";
  return "em_andamento";
}

function EventsList({ events, editEvent, handleDelete, onOpponent, onConvocation }: {
  events: any[];
  editEvent: (e: any) => void;
  handleDelete: (id: string) => void;
  onOpponent: (e: any) => void;
  onConvocation: (e: any) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<"todos" | EventStatus>("todos");
  const [typeFilter, setTypeFilter] = useState<string>("todos");
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  const [feesEvent, setFeesEvent] = useState<any>(null);
  const summonsFn = useServerFn(listSummonsByEvent);
  const tcFn = useServerFn(getMyTrainingCenter);

  const handleEventPdf = async (e: any) => {
    setPdfBusy(e.id);
    try {
      const [tcRes, sumRes] = await Promise.all([
        tcFn().catch(() => null),
        summonsFn({ data: { championship_id: e.id } }),
      ]);
      const tc: any = (tcRes as any)?.tc ?? tcRes ?? null;
      const summons = ((sumRes as any).summons ?? []).map((s: any) => ({
        category: s.event_categories?.category ?? null,
        team_side: s.team_side ?? null,
        opponent: s.opponent ?? null,
        match_date: s.match_date ?? null,
        match_time: s.match_time ?? null,
        field_name: s.event_categories?.field_name ?? null,
        players: (s.game_summon_students ?? [])
          .map((gs: any) => gs.students)
          .filter(Boolean)
          .map((st: any) => ({ name: st.name, jersey: st.jersey_number, goalkeeper: !!st.is_goalkeeper })),
      }));
      await openEventPdf({
        tcName: tc?.name ?? "",
        tcLogoUrl: await toMediaUrl(tc?.logo_url ?? null),
        eventName: e.name,
        eventType: catLabel(e.event_category ?? "amistoso"),
        startDate: e.start_date,
        endDate: e.end_date,
        location: e.location_name ?? null,
        createdAt: e.created_at ?? null,
        notes: e.notes ?? null,
        summons,
      });
    } catch (err: any) {
      toast.error(err?.message || "Erro ao gerar o PDF do evento");
    } finally { setPdfBusy(null); }
  };

  const eventTypes = useMemo(() => {
    const s = new Set<string>();
    events.forEach((e) => { if (e.event_category) s.add(e.event_category); });
    return Array.from(s);
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (statusFilter !== "todos" && computeEventStatus(e) !== statusFilter) return false;
      if (typeFilter !== "todos" && e.event_category !== typeFilter) return false;
      return true;
    });
  }, [events, statusFilter, typeFilter]);

  const catLabel = (c: string) => c === "campeonato_interno" ? "Campeonato Interno"
    : c === "jogos_internos" ? "Jogos Internos"
    : c === "campeonato_externo" ? "Competir em Campeonato" : "Amistoso";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Eventos Criados</CardTitle>
        <p className="text-sm text-muted-foreground">Gerencie os eventos criados pelo seu Centro de Treinamento.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-sm">Filtrar por Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="em_andamento">Em andamento</SelectItem>
                <SelectItem value="futuro">Futuros</SelectItem>
                <SelectItem value="encerrado">Encerrados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Filtrar por Tipo de Evento</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os tipos</SelectItem>
                {eventTypes.map((t) => (
                  <SelectItem key={t} value={t}>{catLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-8">Nenhum evento encontrado.</p>
        )}
        {filtered.map((e) => {
          const status = computeEventStatus(e);
          return (
            <div key={e.id} className="border rounded-md p-4 space-y-2">
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <h3 className="font-bold text-lg">{e.name}</h3>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="secondary">{catLabel(e.event_category ?? "amistoso")}</Badge>
                  {status === "encerrado" && <Badge variant="destructive">Evento encerrado</Badge>}
                  {status === "em_andamento" && <Badge className="bg-green-600 hover:bg-green-600">Em andamento</Badge>}
                  {status === "futuro" && <Badge variant="outline">Futuro</Badge>}
                  {e.event_type && <Badge>{e.event_type.toUpperCase()}</Badge>}
                </div>
              </div>
              <div className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
                <p>🗓 <b>Criado em:</b> {e.created_at ? new Date(e.created_at).toLocaleDateString("pt-BR") : "—"}</p>
                <p>📅 <b>Data do evento:</b> {e.start_date ? new Date(e.start_date + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                  {e.end_date && e.end_date !== e.start_date ? ` até ${new Date(e.end_date + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                </p>
              </div>
              {e.location_name && <p className="text-sm">📍 {e.location_name}</p>}
              <p className="text-sm">
                <b>Convidado:</b> {e.training_centers?.name ?? e.invited_name ?? "—"}{" "}
                {e.invited_tc_id && <Badge variant="outline" className="ml-1">Cadastrado</Badge>}
              </p>
              {e.athletes_playing && (
                <p className="text-sm"><b>Formato:</b> {e.athletes_playing}x{e.athletes_playing} - {e.footwear}</p>
              )}
              <p className="text-sm"><b>Categorias:</b> {e.event_categories?.length ?? 0}</p>
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button size="sm" variant="default" onClick={() => onConvocation(e)}>
                  <Trophy className="h-4 w-4 mr-1" />Convocação para Jogos
                </Button>
                <Button size="sm" variant="secondary" onClick={() => onOpponent(e)}>
                  <UserPlus className="h-4 w-4 mr-1" />Inscrever Atletas do Adversário
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-transform hover:scale-[1.03]"
                  disabled={pdfBusy === e.id} onClick={() => handleEventPdf(e)}>
                  {pdfBusy === e.id
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <FileText className="h-4 w-4 mr-1" />}PDF Evento
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setFeesEvent(e)}>
                  <Wallet className="h-4 w-4 mr-1" />Verificar e quitar Taxas
                </Button>
                <Button size="sm" variant="outline" onClick={() => editEvent(e)}>
                  <Pencil className="h-4 w-4 mr-1" />Editar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(e.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />Excluir
                </Button>
              </div>
            </div>
          );
        })}
        {feesEvent && (
          <EventFeesSettlementDialog
            event={feesEvent}
            open={!!feesEvent}
            onOpenChange={(o) => !o && setFeesEvent(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}


function EventFeesPanel({ events }: { events: any[] }) {
  const loadFn = useServerFn(listFees);
  const saveFn = useServerFn(upsertFee);
  const delFn = useServerFn(deleteFee);
  const linkFn = useServerFn(linkFeeToEvent);

  const [eventId, setEventId] = useState("");
  const [type, setType] = useState<"campeonato" | "arbitragem" | "material">("campeonato");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [eventFees, setEventFees] = useState<any[]>([]);
  const [availableFees, setAvailableFees] = useState<any[]>([]);
  const [linkId, setLinkId] = useState("");
  const [chargeFee, setChargeFee] = useState<any | null>(null);


  const reload = async () => {
    if (!eventId) { setEventFees([]); setAvailableFees([]); return; }
    const linked = await loadFn({ data: { championship_id: eventId } });
    setEventFees(linked.fees ?? []);
    const all = await loadFn({ data: { type } });
    setAvailableFees((all.fees ?? []).filter((f: any) => !f.championship_id));
  };
  useEffect(() => { reload().catch(() => {}); }, [eventId, type]);

  const create = async () => {
    if (!eventId) return toast.error("Escolha o evento");
    if (!name.trim()) return toast.error("Nome obrigatório");
    await saveFn({ data: { type, name, amount: Number(amount || 0), championship_id: eventId } });
    setName(""); setAmount("");
    toast.success("Taxa criada e vinculada");
    reload();
  };

  const link = async () => {
    if (!linkId || !eventId) return;
    await linkFn({ data: { fee_id: linkId, championship_id: eventId } });
    setLinkId("");
    toast.success("Taxa vinculada");
    reload();
  };

  const unlink = async (id: string) => {
    await linkFn({ data: { fee_id: id, championship_id: null } });
    reload();
  };

  const remove = async (id: string) => {
    if (!confirm("Excluir taxa?")) return;
    await delFn({ data: { id } });
    reload();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gerenciar Taxas do Evento</CardTitle>
        <p className="text-sm text-muted-foreground">Vincule taxas existentes ou crie novas para um evento. Elas aparecerão em Loja → Taxa Campeonato.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Evento">
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger><SelectValue placeholder="-- Escolha um evento --" /></SelectTrigger>
              <SelectContent>
                {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tipo de Taxa">
            <Select value={type} onValueChange={(v) => setType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="campeonato">Campeonato</SelectItem>
                <SelectItem value="arbitragem">Arbitragem</SelectItem>
                <SelectItem value="material">Material</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {eventId && (
          <>
            <fieldset className="rounded-md border p-4 space-y-3">
              <legend className="px-2 text-sm font-semibold">Criar nova taxa para este evento</legend>
              <div className="grid gap-3 md:grid-cols-[1fr_160px_auto]">
                <Field label="Nome"><Input placeholder="Ex: Taxa Copinha 2026" value={name} onChange={(e) => setName(e.target.value)} /></Field>
                <Field label="Valor (R$)"><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
                <div className="flex items-end"><Button onClick={create}><Plus className="h-4 w-4 mr-1" />Criar e Vincular</Button></div>
              </div>
            </fieldset>

            <fieldset className="rounded-md border p-4 space-y-3">
              <legend className="px-2 text-sm font-semibold">Vincular taxa avulsa existente</legend>
              <div className="flex gap-2">
                <Select value={linkId} onValueChange={setLinkId}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="-- Escolha uma taxa --" /></SelectTrigger>
                  <SelectContent>
                    {availableFees.length === 0 && <SelectItem disabled value="none">Nenhuma taxa avulsa disponível</SelectItem>}
                    {availableFees.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} (R$ {Number(f.amount).toFixed(2)})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={link} disabled={!linkId}>Vincular</Button>
              </div>
            </fieldset>

            <div>
              <h4 className="mb-2 text-sm font-semibold">Taxas vinculadas a este evento</h4>
              {eventFees.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma taxa vinculada ainda.</p>
              ) : (
                <div className="space-y-2">
                  {eventFees.map((f) => (
                    <div key={f.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                      <div>
                        <div className="font-medium">{f.name} <Badge variant="outline" className="ml-1 capitalize">{f.type}</Badge></div>
                        <div className="text-xs text-muted-foreground">R$ {Number(f.amount).toFixed(2)}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="default" onClick={() => setChargeFee({ ...f, championship_id: eventId })}>
                          <MessageCircle className="h-4 w-4 mr-1" />Cobrar via WA
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => unlink(f.id)}>Desvincular</Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(f.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
      <FeeChargeWhatsappModal
        fee={chargeFee}
        eventName={events.find((e) => e.id === eventId)?.name}
        open={!!chargeFee}
        onOpenChange={(v) => !v && setChargeFee(null)}
      />
    </Card>
  );
}

